import type { PatternThumbnailDto } from '@miyuki/types';

/**
 * Miniatura del patrón para la lista.
 *
 * Un patrón se reconoce por su dibujo mucho antes que por su nombre, así que la
 * miniatura usa la geometría de su tejido (RF-20). La rejilla ya viene
 * submuestreada por el dominio (24 filas como máximo), así que aquí caben unos
 * pocos `div` sin coste: no hace falta un canvas.
 *
 * El desfase sigue la posición **de la miniatura**, no la del patrón:
 * `buildThumbnail` toma una fila de cada `k`, de modo que la paridad original no
 * sobrevive al recorte. A este tamaño el escalonado se lee como textura y no
 * mostacilla por mostacilla, así que basta con que la forma se distinga.
 */
export interface DesignThumbnailProps {
  readonly thumbnail: PatternThumbnailDto;
  readonly staggered: boolean;
}

const WIDTH = 56;

export function DesignThumbnail({ thumbnail, staggered }: DesignThumbnailProps) {
  // El saliente de media mostacilla entra dentro de los 56 px: la miniatura no
  // crece ni se sale de su sitio en la fila de la lista.
  const cell = WIDTH / (thumbnail.columns + (staggered ? 0.5 : 0));

  return (
    <div
      aria-hidden="true"
      className="border-border flex shrink-0 flex-col gap-px overflow-hidden rounded-md border p-px"
      style={{ width: WIDTH }}
    >
      {thumbnail.rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className="flex gap-px"
          style={{ marginLeft: staggered && rowIndex % 2 === 1 ? cell / 2 : 0 }}
        >
          {row.map((cellValue, columnIndex) => (
            <div
              key={`${rowIndex}-${columnIndex}`}
              style={{
                width: cell,
                height: cell,
                backgroundColor:
                  cellValue === null
                    ? 'var(--card)'
                    : (thumbnail.colors[cellValue] ?? 'var(--card)'),
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
